/**
 * Turn a session API error into a French message, per `docs/API.md` §1's
 * `{detail, code}` shape: `no_door_left`, `session_closed`, `not_a_member`,
 * `not_allowed` get a translated, screen-appropriate message; anything else
 * falls back to the server's own `detail`, then to a generic message.
 */
import { ApiError } from '../../api/client';
import { t } from '../../i18n/fr';

import type { ApiErrorBody } from '../../api/types';

/**
 * Resolve the French message to show for a caught session API error.
 *
 * @param error - Whatever a `catch` block caught, typically an {@link ApiError}.
 * @returns A ready-to-display French message.
 */
export function sessionErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const body = error.body as ApiErrorBody | undefined;
    switch (body?.code) {
      case 'no_door_left':
        return t('session.errors.no_door_left');
      case 'session_closed':
        return t('session.errors.session_closed');
      case 'not_a_member':
        return t('session.errors.not_a_member');
      case 'not_allowed':
        return t('session.errors.not_allowed');
      default:
        break;
    }
    if (body?.detail) {
      return body.detail;
    }
  }
  return t('session.errors.generic');
}

/**
 * Whether a caught error is the specific `409 no_door_left` conflict, which
 * the guidance screen shows as a dedicated screen rather than a toast.
 *
 * @param error - Whatever a `catch` block caught.
 * @returns `true` for a `no_door_left` {@link ApiError}.
 */
export function isNoDoorLeftError(error: unknown): boolean {
  if (!(error instanceof ApiError)) {
    return false;
  }
  const body = error.body as ApiErrorBody | undefined;
  return body?.code === 'no_door_left';
}

/**
 * Whether a caught error is the `409 no_current_door` conflict: the caller
 * asked to pass on a door they no longer hold. The guidance screen treats it
 * as "then give me the next one" rather than as a failure, because the most
 * likely cause is a screen still showing a door that has just been recorded.
 *
 * @param error - Whatever a `catch` block caught.
 * @returns `true` for a `no_current_door` {@link ApiError}.
 */
export function isNoCurrentDoorError(error: unknown): boolean {
  if (!(error instanceof ApiError)) {
    return false;
  }
  const body = error.body as ApiErrorBody | undefined;
  return body?.code === 'no_current_door';
}
